import { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { Button, Title } from '../../../../ui'
import GlobalContext from '../../../../../../context/global-context'
import integrationsAPI from '../../../../../../api/integrations.api'
import global66AccessImg from '../../../../../../assets/integrations/global66_access.jpg'
import global66Step1Img from '../../../../../../assets/integrations/global66_step1.png'
import global66Step2aImg from '../../../../../../assets/integrations/global66_step2a.png'
import global66Step2bImg from '../../../../../../assets/integrations/global66_step2b.png'
import global66Step2cImg from '../../../../../../assets/integrations/global66_step2c.png'
import global66Step2dImg from '../../../../../../assets/integrations/global66_step2d.png'
import global66Step3Img from '../../../../../../assets/integrations/global66_step3.png'
import global66Step4Img from '../../../../../../assets/integrations/global66_step4.png'
import { sileo } from 'sileo'

const TOTAL_STEPS = 7
const WEBHOOK_ERROR = 'No se pudo obtener la URL. Intenta de nuevo.'

const formatBalanceInput = value => {
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return Number(digits).toLocaleString('es-CL')
}

const parseBalanceInput = value => {
  const digits = String(value).replace(/\D/g, '')
  return digits ? parseInt(digits, 10) : NaN
}

const StepIndicator = ({ currentStep, total }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {Array.from({ length: total }, (_, i) => i + 1).map(s => (
      <div key={s} className="flex items-center gap-2">
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            s === currentStep
              ? 'bg-primary text-white'
              : s < currentStep
              ? 'bg-green-500 text-white'
              : 'bg-gray-200 text-gray-500'
          }`}
        >
          {s}
        </div>
        {s < total && (
          <div className={`h-0.5 w-8 ${s < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
        )}
      </div>
    ))}
  </div>
)

const StepImage = ({ src, alt, onClick }) => (
  <div className="flex justify-center mt-4">
    <button
      type="button"
      onClick={() => onClick(src, alt)}
      className="cursor-zoom-in transition-opacity hover:opacity-90"
      title="Haz clic para ampliar"
    >
      <img
        src={src}
        alt={alt}
        className="max-h-[36rem] w-full max-w-4xl object-contain rounded-lg shadow-md"
      />
    </button>
  </div>
)

const ImageLightbox = ({ src, alt, onClose }) => {
  if (!src) return null
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl font-bold hover:opacity-70 transition-opacity"
        aria-label="Cerrar"
      >
        ✕
      </button>
      <img
        src={src}
        alt={alt}
        onClick={e => e.stopPropagation()}
        className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl cursor-zoom-out"
      />
    </div>
  )
}

const Global66IntegrationFlow = ({
  variant = 'page',
  onStatusChange,
  onClose,
  initialIntegrated = null,
  initialBalance = null,
}) => {
  const { currentBusiness } = useContext(GlobalContext)
  const isModal = variant === 'modal'

  const [step, setStep] = useState(1)
  const [apiKey, setApiKey] = useState('')
  const [balanceInput, setBalanceInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(initialIntegrated === null)
  const [integrated, setIntegrated] = useState(initialIntegrated ?? false)
  const [balance, setBalance] = useState(initialBalance)
  const [confirming, setConfirming] = useState(false)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookLoading, setWebhookLoading] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const onStatusChangeRef = useRef(onStatusChange)

  const openLightbox = (src, alt) => setLightbox({ src, alt })
  const closeLightbox = () => setLightbox(null)

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange
  }, [onStatusChange])

  const notifyStatus = useCallback(
    (isIntegrated, newBalance = null) => {
      onStatusChangeRef.current?.(isIntegrated, newBalance)
    },
    [],
  )

  const fetchWebhookUrl = useCallback(async () => {
    if (!currentBusiness?.id) return
    setWebhookLoading(true)
    setWebhookUrl('')
    try {
      const response = await integrationsAPI.getGlobal66WebhookUrl(currentBusiness.id)
      setWebhookUrl(response.data?.url ?? WEBHOOK_ERROR)
    } catch {
      setWebhookUrl(WEBHOOK_ERROR)
    } finally {
      setWebhookLoading(false)
    }
  }, [currentBusiness?.id])

  useEffect(() => {
    if (initialIntegrated !== null) {
      setIntegrated(initialIntegrated)
      setBalance(initialBalance)
      setStatusLoading(false)
      return
    }

    const fetchStatus = async () => {
      if (!currentBusiness?.id) return
      try {
        setStatusLoading(true)
        const response = await integrationsAPI.getIntegrationsStatus(currentBusiness.id)
        const g66 = response.data?.global66
        setIntegrated(g66?.integrated ?? false)
        setBalance(g66?.initialBalance ?? null)
        notifyStatus(g66?.integrated ?? false, g66?.initialBalance ?? null)
      } catch {
        setIntegrated(false)
      } finally {
        setStatusLoading(false)
      }
    }
    fetchStatus()
  }, [currentBusiness?.id, initialIntegrated, initialBalance, notifyStatus])

  useEffect(() => {
    if (!integrated && !statusLoading) {
      fetchWebhookUrl()
    }
  }, [integrated, statusLoading, fetchWebhookUrl])

  const handleCopyWebhook = () => {
    if (!webhookUrl || webhookUrl === WEBHOOK_ERROR) return
    navigator.clipboard.writeText(webhookUrl)
    sileo.success({ title: 'Copiado', description: 'URL copiada al portapapeles' })
  }

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      sileo.info({ title: 'API Key requerida', description: 'Ingresa el API Key de Global66 ⚠️' })
      return
    }
    const parsed = parseBalanceInput(balanceInput)
    if (isNaN(parsed) || parsed < 0) {
      sileo.info({ title: 'Balance inválido', description: 'Ingresa un balance inicial válido ⚠️' })
      return
    }

    setLoading(true)
    try {
      await integrationsAPI.saveGlobal66Credentials(currentBusiness.id, {
        apiKey: apiKey.trim(),
        initialBalance: parsed,
      })
      setIntegrated(true)
      setBalance(parsed)
      setApiKey('')
      setBalanceInput('')
      setStep(1)
      notifyStatus(true, parsed)
      sileo.success({
        title: 'Conexión exitosa',
        description: 'Global66 conectado. Tus transacciones llegarán automáticamente vía webhook.',
      })
      if (isModal) onClose?.()
    } catch (err) {
      sileo.error({
        title: 'Error de conexión',
        description:
          err.response?.data?.message ||
          'No se pudo conectar con Global66. Verifica el API Key 🤯',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    setLoading(true)
    try {
      await integrationsAPI.deleteGlobal66Integration(currentBusiness.id)
      setIntegrated(false)
      setBalance(null)
      setConfirming(false)
      setStep(1)
      notifyStatus(false, null)
      sileo.success({ title: 'Integración eliminada', description: 'Global66 ha sido desvinculado correctamente' })
    } catch (err) {
      sileo.error({
        title: 'Error',
        description: err.response?.data?.message || 'No se pudo desvincular Global66 🤯',
      })
    } finally {
      setLoading(false)
    }
  }

  const btnPrimary = isModal
    ? 'w-full bg-primary hover:bg-primary/80 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors'
    : 'text-sm bg-accent2 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed'

  const btnSecondary = isModal
    ? 'w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-colors'
    : 'text-sm bg-gray-200 text-gray-700 rounded-full'

  const NavButtons = ({ onBack, onNext, nextLabel = 'Siguiente', nextDisabled = false }) => (
    <div className={`flex mt-6 ${isModal ? 'flex-col gap-3' : 'justify-between'}`}>
      {onBack ? (
        <Button onClick={onBack} className={btnSecondary} title="Atrás" />
      ) : (
        !isModal && <div />
      )}
      <Button
        onClick={onNext}
        disabled={nextDisabled || loading}
        className={btnPrimary}
        title={nextLabel}
      />
    </div>
  )

  if (statusLoading) {
    return <div className="py-8 text-center text-gray-500 text-sm">Cargando estado...</div>
  }

  if (integrated) {
    return (
      <div className="space-y-6">
        {!confirming ? (
          <>
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className={`font-bold text-primary ${isModal ? 'text-xl' : 'text-2xl'}`}>
                ¡Global66 está conectado!
              </div>
              {balance !== null && (
                <p className="text-sm text-gray-600">
                  Balance inicial registrado:{' '}
                  <span className="font-semibold">${Number(balance).toLocaleString('es-CL')}</span>
                </p>
              )}
              <img
                src={global66AccessImg}
                alt="Global66"
                className="max-h-12 w-auto rounded-lg shadow-sm mt-2"
              />
            </div>
            {!isModal && (
              <div className="flex justify-end">
                <Button
                  onClick={() => setConfirming(true)}
                  className="text-sm bg-red-500 text-white rounded-full"
                  title="Desvincular Global66"
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-4">
            <p className="text-red-700 font-semibold">
              ¿Estás seguro que deseas desvincular Global66? Se eliminarán las credenciales y la
              cuenta bancaria asociada dejará de sincronizarse.
            </p>
            <div className={`flex gap-3 ${isModal ? 'flex-col' : 'justify-end'}`}>
              <Button
                onClick={() => setConfirming(false)}
                disabled={loading}
                className={btnSecondary}
                title="Cancelar"
              />
              <Button
                onClick={handleDisconnect}
                disabled={loading}
                className={
                  isModal
                    ? 'w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-xl transition-colors disabled:cursor-not-allowed'
                    : 'text-sm bg-red-500 text-white rounded-full disabled:opacity-50'
                }
                title={loading ? 'Desvinculando...' : 'Sí, desvincular'}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative w-full mx-auto">
      {isModal ? (
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">¿Cómo conectar Global66 a Luca?</h2>
      ) : (
        <Title className="text-2xl mb-4">¿Cómo conectar Global66 a Luca?</Title>
      )}

      <StepIndicator currentStep={step} total={TOTAL_STEPS} />

      {step === 1 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700 space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">1.</span>
              <div className="space-y-3">
                <p>
                  Ingresa a tu cuenta de empresa en Global66. Puedes acceder directamente desde aquí:
                </p>
                <a
                  href="https://empresas.global66.com/auth/log-in"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block px-4 py-2 bg-accent2 text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity"
                >
                  Ir al portal de Global66 →
                </a>
                <p>
                  Una vez dentro, en el <strong>panel izquierdo</strong> presiona{' '}
                  <strong>Webhooks</strong> y luego el botón <strong>Crear webhook</strong>.
                </p>
              </div>
            </div>
          </div>
          <StepImage src={global66Step1Img} alt="Global66 panel webhooks" onClick={openLightbox} />
          <NavButtons onNext={() => setStep(2)} />
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700 space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">2.</span>
              <div className="space-y-3">
                <p>En el formulario del webhook, completa la sección de identificación:</p>
                <ul className="space-y-3 text-sm">
                  <li>
                    <span className="font-semibold">Nombre:</span> Tryluca
                  </li>
                  <li>
                    <span className="font-semibold">Método:</span> POST
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <StepImage src={global66Step2aImg} alt="Global66 nombre y método del webhook" onClick={openLightbox} />
          <NavButtons onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700 space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">3.</span>
              <div className="w-full space-y-3">
                <div>
                  <div className="font-semibold mb-1">URL — copia y pega esta dirección:</div>
                  {webhookLoading ? (
                    <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                      <span className="text-xs text-gray-400">Obteniendo URL...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={webhookUrl}
                        className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white font-mono text-gray-700 focus:outline-none"
                      />
                      <button
                        type="button"
                        disabled={!webhookUrl || webhookUrl === WEBHOOK_ERROR}
                        onClick={handleCopyWebhook}
                        className="px-3 py-2 bg-primary hover:bg-primary/80 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap"
                      >
                        Copiar
                      </button>
                    </div>
                  )}
                </div>
                <p>
                  Pega la URL en el formulario de Global66 y presiona el botón <strong>Probar</strong>.
                  Verifica que aparezca el mensaje confirmando que la URL fue validada correctamente.
                </p>
              </div>
            </div>
          </div>
          <StepImage src={global66Step2bImg} alt="Global66 URL del webhook y prueba de conexión" onClick={openLightbox} />
          <NavButtons onBack={() => setStep(2)} onNext={() => setStep(4)} />
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">4.</span>
              <div>
                <p className="font-semibold mb-1">Marca los siguientes eventos:</p>
                <ul className="list-disc list-inside ml-2 space-y-1 text-sm">
                  <li>
                    Recepción de dinero por transferencia bancaria{' '}
                    <span className="text-red-600 font-semibold">(obligatorio)</span>
                  </li>
                  <li>
                    Cambio de estado de una remesa <span className="text-gray-400">(opcional)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <StepImage src={global66Step2cImg} alt="Global66 eventos a notificar" onClick={openLightbox} />
          <NavButtons onBack={() => setStep(3)} onNext={() => setStep(5)} />
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">5.</span>
              <p>
                Revisa que todo esté correcto y presiona <strong>Continuar</strong> para crear el
                webhook.
              </p>
            </div>
          </div>
          <StepImage src={global66Step2dImg} alt="Global66 finalizar creación del webhook" onClick={openLightbox} />
          <NavButtons onBack={() => setStep(4)} onNext={() => setStep(6)} />
        </div>
      )}

      {step === 6 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">6.</span>
              <p>
                Ingresa el <strong>PIN de tu empresa</strong> para autorizar la creación del webhook.
                Es el mismo PIN que usas para confirmar operaciones en el portal de Global66.
              </p>
            </div>
          </div>
          <StepImage src={global66Step3Img} alt="Global66 PIN empresa" onClick={openLightbox} />
          <NavButtons onBack={() => setStep(5)} onNext={() => setStep(7)} />
        </div>
      )}

      {step === 7 && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-gray-700 space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-xl font-bold text-primary">7.</span>
              <div className="space-y-2">
                <p>
                  Copia el <strong>API Key</strong> que aparece en pantalla y pégalo en el campo de
                  abajo.
                </p>
                <p className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
                  Si no copiaste el API Key a tiempo, elimina el webhook creado y repite el proceso
                  desde el paso 1.
                </p>
              </div>
            </div>
          </div>
          <StepImage src={global66Step4Img} alt="Global66 API Key" onClick={openLightbox} />

          <div className="bg-white p-4 border border-gray-200 rounded-lg space-y-4 mt-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                API Key de Global66
              </label>
              <input
                type="text"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="Pega aquí el API Key del webhook..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Balance inicial de la cuenta (CLP)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={balanceInput}
                onChange={e => setBalanceInput(formatBalanceInput(e.target.value))}
                placeholder="Ej: 1.000.000"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="text-xs text-gray-400 mt-1">
                Saldo de tu cuenta Global66 al momento de la integración.
              </p>
            </div>
          </div>

          <NavButtons
            onBack={() => setStep(6)}
            onNext={handleConnect}
            nextLabel={loading ? 'Conectando...' : 'Conectar Global66'}
            nextDisabled={!apiKey.trim() || !balanceInput.trim()}
          />
        </div>
      )}

      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={closeLightbox} />
    </div>
  )
}

export default Global66IntegrationFlow
